import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  TrendingDown, Search, Plus, Minus, Pencil, Ban, Trash2, RefreshCw, DollarSign, Calendar, AlertTriangle, Clock, Send, Undo2,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ComboBox } from '../../components/ComboBox';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import { formatMoeda, formatDataSP } from '@fiodecobre/shared';
import { ModalContaPagar } from './ModalContaPagar';
import { ModalBaixarContaPagar } from './ModalBaixarContaPagar';
import { ModalEnviarWhatsApp } from '../../components/whatsapp/ModalEnviarWhatsApp';

interface ContaPagar {
  id: number;
  numero_documento: string | null;
  parcela_numero: number;
  parcela_total: number;
  categoria_despesa_id: number;
  categoria_nome: string | null;
  fornecedor_id: number | null;
  fornecedor_razao_social: string | null;
  fornecedor_nome_fantasia: string | null;
  // O CNPJ e o da FILIAL do titulo (a inscricao que emitiu o boleto), nao o da empresa (DA-311).
  fornecedor_cpf_cnpj: string | null;
  filial_id: number | null;
  filial_apelido: string | null;
  filial_uf: string | null;
  fornecedor_filiais_qtd: number | null;
  fornecedor_produto_id: number | null;
  fornecedor_produto_razao_social: string | null;
  fornecedor_produto_nome_fantasia: string | null;
  descricao: string;
  valor: string;
  data_emissao: string;
  data_vencimento: string;
  data_pagamento: string | null;
  valor_pago: string | null;
  forma_pagamento_id: number | null;
  forma_pagamento_nome: string | null;
  status: string;
  observacoes: string | null;
  nota_entrada_id: number | null;
  nota_entrada_numero: string | null;
  nota_entrada_serie: string | null;
  operador_cadastro_nome: string | null;
  operador_baixa_nome: string | null;
  origem_legado: string | null;
  vencido: boolean;
  dias_atraso: number;
}

interface Resumo {
  qtd_pendente: string; total_pendente: string;
  qtd_vencido: string; total_vencido: string;
  qtd_a_vencer_7d: string; total_a_vencer_7d: string;
  qtd_pago_mes: string; total_pago_mes: string;
}

interface Categoria { id: number; nome: string }
interface Fornecedor { id: number; razao_social: string; nome_fantasia: string | null }

const hojeISO = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());

function hojeMaisDias(dias: number): string {
  const d = new Date(`${hojeISO()}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

// '' = sem limite superior (o campo mostra a data mais recente da lista).
const fimPadraoDaAba = (aba: string) => (aba === 'pendente' ? hojeMaisDias(30) : '');

export function ContasPagarLista() {
  const { usuario } = useAuth();
  const toast = useToast();
  const podeEditar = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  const [contas, setContas] = useState<ContaPagar[]>([]);
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);

  const [filtro, setFiltro] = useState<string>('pendente');
  const [busca, setBusca] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');
  const [fornecedorFiltro, setFornecedorFiltro] = useState<string>('');
  const [fornecedorProdutoFiltro, setFornecedorProdutoFiltro] = useState<string>('');
  // Periodo nasce "da data mais antiga a mais recente da aba" (pedidos dele, 05/10/2026), exceto
  // Pendentes, que vai ate HOJE + 30 dias (vencidas + o proximo mes). Na aba Pagas vale pela data
  // do pagamento; nas outras, pelo vencimento (quem decide e' o backend).
  // Ponta sem limite: a consulta vai sem ele e o campo EXIBE a data extrema que veio na lista
  // (extremosDaAba, abaixo).
  const [inicio, setInicio] = useState('');
  const [inicioMaisAntiga, setInicioMaisAntiga] = useState(true);
  const [fim, setFim] = useState('');
  const [fimMaisRecente, setFimMaisRecente] = useState(true);
  // fimMaisRecente = "ate" no padrao da aba; senao vale o que esta em fim ('' = sem limite).
  const fimEfetivo = fimMaisRecente ? fimPadraoDaAba(filtro) : fim;
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());

  const [modalNovo, setModalNovo] = useState(false);
  const [contaEditar, setContaEditar] = useState<ContaPagar | null>(null);
  const [contaBaixar, setContaBaixar] = useState<ContaPagar | null>(null);
  const [contaCancelar, setContaCancelar] = useState<ContaPagar | null>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [contaExcluir, setContaExcluir] = useState<ContaPagar | null>(null);
  const [contaWhats, setContaWhats] = useState<ContaPagar | null>(null);
  const [contaEstornar, setContaEstornar] = useState<ContaPagar | null>(null);
  const [motivoEstorno, setMotivoEstorno] = useState('');
  const [estornando, setEstornando] = useState(false);

  function montarMensagemConta(c: ContaPagar): string {
    const linhas: string[] = ['*Conta a Pagar*'];
    if (c.numero_documento) linhas.push(`Documento: ${c.numero_documento}`);
    if (c.parcela_total > 1) linhas.push(`Parcela: ${c.parcela_numero}/${c.parcela_total}`);
    linhas.push(`Descricao: ${c.descricao}`);
    if (c.fornecedor_razao_social || c.fornecedor_nome_fantasia) {
      linhas.push(`Fornecedor: ${c.fornecedor_nome_fantasia || c.fornecedor_razao_social}`);
    }
    if (c.categoria_nome) linhas.push(`Categoria: ${c.categoria_nome}`);
    linhas.push(`Vencimento: ${formatDataSP(c.data_vencimento)}`);
    linhas.push(`*Valor: ${formatMoeda(c.valor)}*`);
    if (c.observacoes) linhas.push(`Obs: ${c.observacoes}`);
    return linhas.join('\n');
  }

  async function carregar() {
    setLoading(true);
    try {
      const query: Record<string, string | undefined> = {};
      if (filtro !== 'todos') query.status = filtro;
      if (categoriaFiltro) query.categoria_despesa_id = categoriaFiltro;
      if (fornecedorFiltro) query.fornecedor_id = fornecedorFiltro;
      if (fornecedorProdutoFiltro) query.fornecedor_produto_id = fornecedorProdutoFiltro;
      if (inicio && !inicioMaisAntiga) query.inicio = inicio;
      if (fimEfetivo) query.fim = fimEfetivo;
      const [r1, r2] = await Promise.all([
        api.get<ContaPagar[]>('/api/contas-pagar', { query }),
        api.get<Resumo>('/api/contas-pagar/resumo'),
      ]);
      setContas(r1);
      setResumo(r2);
      setExpandedIds(new Set());
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function carregarCategorias() {
    try {
      setCategorias(await api.get<Categoria[]>('/api/categorias-despesa'));
    } catch {}
  }

  async function carregarFornecedores() {
    try {
      setFornecedores(await api.get<Fornecedor[]>('/api/fornecedores', { query: { todos: true } }));
    } catch {}
  }

  useEffect(() => { void carregarCategorias(); void carregarFornecedores(); }, []);
  useEffect(() => { void carregar(); }, [filtro, categoriaFiltro, fornecedorFiltro, fornecedorProdutoFiltro, inicio, inicioMaisAntiga, fim, fimMaisRecente]);

  // Sem o limite, a lista ja traz as pontas da aba, entao as datas vem dela mesma.
  const extremosDaAba = useMemo(() => {
    let menor = '';
    let maior = '';
    for (const c of contas) {
      const d = String((filtro === 'pago' ? c.data_pagamento : c.data_vencimento) ?? '').slice(0, 10);
      if (!d) continue;
      if (!menor || d < menor) menor = d;
      if (!maior || d > maior) maior = d;
    }
    return { menor, maior };
  }, [contas, filtro]);

  const buscaLower = busca.trim().toLowerCase();
  const contasFiltradas = useMemo(() => contas.filter((c) => {
    if (!buscaLower) return true;
    return (
      (c.descricao || '').toLowerCase().includes(buscaLower) ||
      (c.numero_documento || '').toLowerCase().includes(buscaLower) ||
      (c.fornecedor_razao_social || '').toLowerCase().includes(buscaLower) ||
      (c.fornecedor_nome_fantasia || '').toLowerCase().includes(buscaLower) ||
      (c.fornecedor_produto_razao_social || '').toLowerCase().includes(buscaLower) ||
      (c.fornecedor_produto_nome_fantasia || '').toLowerCase().includes(buscaLower)
    );
  }), [contas, buscaLower]);

  // Padrao de grid do sistema: clicar marca a linha (amarelo), ↑/↓ navegam, Enter/duplo-clique
  // editam — so titulo pendente e pra quem pode editar (onde o lapis existe). Com qualquer modal
  // aberto o teclado da grade desliga, senao o Enter num botao do modal reabriria a edicao.
  const algumModalAberto = modalNovo || !!(contaEditar || contaBaixar || contaCancelar || contaExcluir || contaWhats || contaEstornar);
  const grid = useGridNavegavel<ContaPagar>({
    itens: contasFiltradas,
    getId: (c) => c.id,
    onAtivar: (c) => {
      if (!podeEditar || c.status !== 'pendente') return;
      window.getSelection()?.removeAllRanges();
      setContaEditar(c);
    },
    ativo: !algumModalAberto,
  });

  // "MARTINS · MG" — a empresa com mais de uma inscricao precisa dizer de QUAL CNPJ e o titulo:
  // e ele que emite o boleto. Empresa com um CNPJ so mostra so o nome (DA-311).
  const filialSufixo = (c: ContaPagar): string | null => {
    if ((c.fornecedor_filiais_qtd ?? 0) <= 1) return null;
    return c.filial_apelido || c.filial_uf || null;
  };

  const fornecedorDisplay = (c: ContaPagar) => {
    const nome = c.fornecedor_nome_fantasia || c.fornecedor_razao_social || '—';
    const suf = filialSufixo(c);
    return suf ? `${nome} · ${suf}` : nome;
  };

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function confirmarCancelamento() {
    if (!contaCancelar) return;
    if (motivoCancelamento.trim().length < 3) { toast.erro('Informe o motivo (min. 3 caracteres)'); return; }
    try {
      await api.post(`/api/contas-pagar/${contaCancelar.id}/cancelar`, { motivo: motivoCancelamento });
      toast.sucesso('Conta cancelada');
      setContaCancelar(null); setMotivoCancelamento('');
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
  }

  async function confirmarEstorno() {
    if (!contaEstornar) return;
    if (motivoEstorno.trim().length < 3) { toast.erro('Informe o motivo do estorno'); return; }
    setEstornando(true);
    try {
      const r: any = await api.post(`/api/contas-pagar/${contaEstornar.id}/estornar`, { motivo: motivoEstorno });
      toast.sucesso(`Pagamento estornado — ${formatMoeda(r?.contrapartida?.valor ?? contaEstornar.valor_pago ?? contaEstornar.valor)} voltou ao caixa. A conta esta em Pendentes.`);
      setContaEstornar(null); setMotivoEstorno('');
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
    finally { setEstornando(false); }
  }

  async function confirmarExclusao() {
    if (!contaExcluir) return;
    try {
      await api.delete(`/api/contas-pagar/${contaExcluir.id}`);
      toast.sucesso('Conta excluida');
      setContaExcluir(null);
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
  }

  function statusBadge(c: ContaPagar) {
    if (c.status === 'pago') return <span className="badge-verde">Pago</span>;
    if (c.status === 'cancelada') return <span className="badge-vermelho">Cancelada</span>;
    if (c.vencido) return <span className="badge-vermelho">Vencida ({c.dias_atraso}d)</span>;
    return <span className="badge-amarelo">Pendente</span>;
  }

  return (
    <div>
      <div className="pagina-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="pagina-titulo">
          <TrendingDown size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3, color: '#ea580c' }} />
          Contas a Pagar
        </h2>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn-secundario" onClick={() => void carregar()} title="Atualizar"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
            <RefreshCw size={14} /> Atualizar
          </button>
          {podeEditar && (
            <button type="button" className="btn-primario" onClick={() => setModalNovo(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <Plus size={14} /> Nova conta
            </button>
          )}
        </div>
      </div>

      {/* 4 Cards KPI */}
      {resumo && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
          {/* Na aba Pendentes o card soma o que a LISTA mostra (pedido dele, 05/10/2026);
              nas outras abas a lista nao e' de pendentes, entao fica o total geral. */}
          {filtro === 'pendente' ? (
            <CardKPI
              cor="#fbbf24"
              icon={<Clock size={18} />}
              label="Pendentes"
              qtd={String(contasFiltradas.length)}
              valor={String(contasFiltradas.reduce((s, c) => s + Math.round(Number(c.valor) * 100), 0) / 100)}
              nota="na lista"
            />
          ) : (
            <CardKPI cor="#fbbf24" icon={<Clock size={18} />} label="Pendentes" qtd={resumo.qtd_pendente} valor={resumo.total_pendente} />
          )}
          <CardKPI cor="#ef4444" icon={<AlertTriangle size={18} />} label="Vencidas" qtd={resumo.qtd_vencido} valor={resumo.total_vencido} />
          <CardKPI cor="#3b82f6" icon={<Calendar size={18} />} label="A vencer 7d" qtd={resumo.qtd_a_vencer_7d} valor={resumo.total_a_vencer_7d} />
          <CardKPI cor="#10b981" icon={<DollarSign size={18} />} label="Pagas no mes" qtd={resumo.qtd_pago_mes} valor={resumo.total_pago_mes} />
        </div>
      )}

      {/* Abas de status */}
      <div className="abas">
        {[
          { st: 'pendente', label: 'Pendentes' },
          { st: 'vencido', label: 'Vencidas' },
          { st: 'pago', label: 'Pagas' },
          { st: 'cancelada', label: 'Canceladas' },
          { st: 'todos', label: 'Todas' },
        ].map((aba) => (
          <button
            key={aba.st}
            type="button"
            className={`aba-btn ${filtro === aba.st ? 'aba-btn-ativo' : ''}`}
            onClick={() => setFiltro(aba.st)}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {/* Busca + filtros avancados */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="search"
              placeholder="Buscar descricao, numero, fornecedor..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid var(--input-border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--input-fg)' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {filtro === 'pago' ? 'Pagamento de' : 'Vencimento de'}
            </span>
            <input
              type="date"
              value={inicioMaisAntiga ? extremosDaAba.menor : inicio}
              onChange={(e) => { setInicio(e.target.value); setInicioMaisAntiga(false); }}
              title={inicioMaisAntiga ? 'Data mais antiga desta aba' : undefined}
            />
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>ate</span>
            <input
              type="date"
              value={fimEfetivo || extremosDaAba.maior}
              onChange={(e) => { setFim(e.target.value); setFimMaisRecente(false); }}
              title={fimEfetivo ? undefined : 'Data mais recente desta aba'}
            />
            <button
              type="button"
              className="btn-secundario"
              onClick={() => { setInicio(hojeISO()); setInicioMaisAntiga(false); setFim(hojeISO()); setFimMaisRecente(false); }}
              title="Voltar o periodo para hoje"
            >
              Hoje
            </button>
            <button
              type="button"
              className="btn-secundario"
              onClick={() => { setInicio(''); setInicioMaisAntiga(true); setFim(''); setFimMaisRecente(false); }}
              disabled={inicioMaisAntiga && !fimEfetivo}
              title="Mostrar todas as datas"
            >
              Todas as datas
            </button>
          </div>
          <button type="button" className="btn-secundario" onClick={() => setMostrarFiltros((v) => !v)}>
            {mostrarFiltros ? 'Ocultar filtros' : 'Mais filtros'}
          </button>
          <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 13 }}>
            {contasFiltradas.length} conta{contasFiltradas.length === 1 ? '' : 's'}
          </span>
        </div>
        {mostrarFiltros && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginTop: 10 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Categoria</label>
              <ComboBox
                opcoes={[{ value: '', label: 'Todas' }, ...categorias.map((c) => ({ value: c.id, label: c.nome }))]}
                value={categoriaFiltro}
                onChange={(v) => setCategoriaFiltro(String(v))}
                placeholder="Todas"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)' }} title="Quem recebe o pagamento">Fornecedor do Titulo</label>
              <ComboBox
                opcoes={[{ value: '', label: 'Todos' }, ...fornecedores.map((f) => ({ value: f.id, label: f.nome_fantasia || f.razao_social }))]}
                value={fornecedorFiltro}
                onChange={(v) => setFornecedorFiltro(String(v))}
                placeholder="Todos"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)' }} title="Fornecedor relacionado ao gasto (ex: BETEL nos fretes da EXECUTIVA)">Fornecedor do Produto</label>
              <ComboBox
                opcoes={[{ value: '', label: 'Todos' }, ...fornecedores.map((f) => ({ value: f.id, label: f.nome_fantasia || f.razao_social }))]}
                value={fornecedorProdutoFiltro}
                onChange={(v) => setFornecedorProdutoFiltro(String(v))}
                placeholder="Todos"
              />
            </div>
          </div>
        )}
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th style={{ width: 130 }}>Documento</th>
              <th>Descricao</th>
              <th style={{ width: 200 }}>Fornecedor</th>
              <th style={{ width: 200, maxWidth: 200 }}>Categoria</th>
              <th style={{ width: 110 }}>Vencimento</th>
              {filtro === 'pago' && <th style={{ width: 110 }}>Pago em</th>}
              <th style={{ width: 110, textAlign: 'right' }}>Valor</th>
              <th style={{ width: 130 }}>Status</th>
              <th style={{ width: 160 }}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {contasFiltradas.length === 0 ? (
              <tr><td colSpan={filtro === 'pago' ? 10 : 9} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhuma conta encontrada</td></tr>
            ) : contasFiltradas.map((c) => (
              <Fragment key={c.id}>
                <tr
                  {...grid.rowProps(c)}
                  style={{ ...(c.vencido && c.status === 'pendente' ? { background: 'rgba(220,38,38,0.06)' } : {}), ...grid.rowProps(c).style }}
                  // Duplo-clique em botao da linha (Pagar, lapis...) nao conta como abrir a edicao.
                  onDoubleClick={(e) => {
                    if ((e.target as HTMLElement).closest('button')) return;
                    grid.rowProps(c).onDoubleClick?.();
                  }}
                >
                  <td>
                    <button type="button" onClick={() => toggleExpand(c.id)} className="btn-icon" title="Detalhes">
                      {expandedIds.has(c.id) ? <Minus size={14} /> : <Plus size={14} />}
                    </button>
                  </td>
                  {/* nowrap: a coluna fica no limite e "(1/4)" cabia numa linha enquanto "(2/4)" quebrava
                      (o "1" e mais estreito na fonte proporcional) — parcelas do mesmo grupo saiam desalinhadas. */}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {c.numero_documento || (c.origem_legado ? <span style={{ color: 'var(--text-muted)' }}>{c.origem_legado}</span> : '-')}
                    {/* A parcela ja vem no numero ("CP-000097 (2/4)"); a linha so aparece se o numero
                        foi editado e perdeu o sufixo (02/10/2026: 187 de 187 parcelados o tinham). */}
                    {c.parcela_total > 1 && !(c.numero_documento || '').includes(`(${c.parcela_numero}/${c.parcela_total})`) && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>parcela {c.parcela_numero}/{c.parcela_total}</div>
                    )}
                  </td>
                  <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.descricao}>{c.descricao}</td>
                  <td style={{ maxWidth: 220 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fornecedorDisplay(c)}>{fornecedorDisplay(c)}</div>
                    {(c.fornecedor_produto_nome_fantasia || c.fornecedor_produto_razao_social) && (
                      <div
                        style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={`Fornecedor do produto: ${c.fornecedor_produto_nome_fantasia || c.fornecedor_produto_razao_social}`}
                      >
                        ↳ {c.fornecedor_produto_nome_fantasia || c.fornecedor_produto_razao_social}
                      </div>
                    )}
                  </td>
                  <td style={{ maxWidth: 200, fontSize: 13 }}>{c.categoria_nome || '-'}</td>
                  <td style={{ fontWeight: 700 }}>{formatDataSP(c.data_vencimento)}</td>
                  {filtro === 'pago' && <td>{c.data_pagamento ? formatDataSP(c.data_pagamento) : '-'}</td>}
                  <td className="cel-num" style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatMoeda(c.valor)}</td>
                  <td>{statusBadge(c)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {podeEditar && c.status === 'pendente' && (
                        <>
                          <button
                            type="button"
                            onClick={() => setContaBaixar(c)}
                            className="btn-primario"
                            title="Baixar (pagar)"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            <DollarSign size={13} /> Pagar
                          </button>
                          <button type="button" onClick={() => setContaEditar(c)} className="btn-icon" title="Editar">
                            <Pencil size={14} />
                          </button>
                          <button type="button" onClick={() => setContaWhats(c)} className="btn-icon" title="Enviar pelo WhatsApp">
                            <Send size={14} />
                          </button>
                          <button type="button" onClick={() => setContaCancelar(c)} className="btn-icon" title="Cancelar conta">
                            <Ban size={14} />
                          </button>
                          <button type="button" onClick={() => setContaExcluir(c)} className="btn-icon" title="Excluir">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                      {usuario?.perfil === 'admin' && c.status === 'pago' && (
                        <button
                          type="button"
                          onClick={() => { setContaEstornar(c); setMotivoEstorno(''); }}
                          className="btn-icon"
                          title="Estornar o pagamento — devolve a conta pra pendente e o valor ao caixa (admin)"
                        >
                          <Undo2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedIds.has(c.id) && (
                  <tr>
                    <td colSpan={filtro === 'pago' ? 10 : 9} style={{ background: 'var(--card-bg)', padding: 12, fontSize: 13 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                        <div><strong>Emissao:</strong> {formatDataSP(c.data_emissao)}</div>
                        <div><strong>Categoria:</strong> {c.categoria_nome || '-'}</div>
                        {c.fornecedor_cpf_cnpj && (
                          <div>
                            <strong>CNPJ/CPF:</strong> {c.fornecedor_cpf_cnpj}
                            {filialSufixo(c) && <span className="muted"> (inscricao {filialSufixo(c)})</span>}
                          </div>
                        )}
                        {c.nota_entrada_numero && (
                          <div><strong>NF de Entrada:</strong> {c.nota_entrada_numero}/{c.nota_entrada_serie || '1'}</div>
                        )}
                        {c.operador_cadastro_nome && <div><strong>Cadastrada por:</strong> {c.operador_cadastro_nome}</div>}
                        {c.status === 'pago' && (
                          <>
                            <div><strong>Pago em:</strong> {formatDataSP(c.data_pagamento)}</div>
                            <div><strong>Valor pago:</strong> <span className="cel-num">{formatMoeda(c.valor_pago || 0)}</span></div>
                            <div><strong>Forma:</strong> {c.forma_pagamento_nome || '-'}</div>
                            <div><strong>Operador:</strong> {c.operador_baixa_nome || '-'}</div>
                          </>
                        )}
                      </div>
                      {c.observacoes && (
                        <div style={{ marginTop: 10, padding: 8, background: 'var(--input-bg)', borderRadius: 4 }}>
                          <strong>Obs:</strong> {c.observacoes}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/* Modais */}
      {modalNovo && (
        <ModalContaPagar
          aberto={modalNovo}
          onFechar={() => setModalNovo(false)}
          onSalvo={() => { setModalNovo(false); void carregar(); }}
          categorias={categorias}
          fornecedores={fornecedores}
        />
      )}
      {contaEditar && (
        <ModalContaPagar
          aberto={true}
          conta={contaEditar}
          onFechar={() => setContaEditar(null)}
          onSalvo={() => { setContaEditar(null); void carregar(); }}
          categorias={categorias}
          fornecedores={fornecedores}
        />
      )}
      {contaBaixar && (
        <ModalBaixarContaPagar
          conta={contaBaixar}
          onFechar={() => setContaBaixar(null)}
          onBaixado={() => { setContaBaixar(null); void carregar(); }}
        />
      )}

      {/* Cancelar */}
      <Modal
        aberto={!!contaCancelar}
        onFechar={() => { setContaCancelar(null); setMotivoCancelamento(''); }}
        titulo="Cancelar conta"
        largura={460}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => { setContaCancelar(null); setMotivoCancelamento(''); }}>Voltar</button>
            <button type="button" className="btn-perigo" onClick={() => void confirmarCancelamento()}>Cancelar conta</button>
          </>
        }
      >
        <div style={{ marginBottom: 8, fontSize: 13 }}>
          Documento <strong>{contaCancelar?.numero_documento || '-'}</strong> · {contaCancelar && <span className="cel-num">{formatMoeda(contaCancelar.valor)}</span>}
        </div>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Motivo (min. 3 caracteres)</label>
        <textarea
          rows={3}
          value={motivoCancelamento}
          onChange={(e) => setMotivoCancelamento(e.target.value)}
          style={{ width: '100%', padding: 8, border: '1px solid var(--input-border)', borderRadius: 4, background: 'var(--input-bg)', color: 'var(--input-fg)' }}
          autoFocus
        />
      </Modal>

      {/* Estornar pagamento (admin) — a conta volta pra pendente; dali o Excluir/Cancelar de sempre. */}
      <Modal
        aberto={!!contaEstornar}
        onFechar={() => { setContaEstornar(null); setMotivoEstorno(''); }}
        titulo="Estornar pagamento"
        largura={520}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => { setContaEstornar(null); setMotivoEstorno(''); }}>Voltar</button>
            <button type="button" className="btn-primario" onClick={() => void confirmarEstorno()} disabled={estornando}>
              {estornando ? 'Estornando...' : 'Confirmar estorno'}
            </button>
          </>
        }
      >
        {contaEstornar && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div>
              <div>Documento <strong>{contaEstornar.numero_documento || '-'}</strong> · {contaEstornar.descricao}</div>
              <div>Fornecedor: {contaEstornar.fornecedor_nome_fantasia || contaEstornar.fornecedor_razao_social || '-'}</div>
              <div>
                Pago em <strong>{contaEstornar.data_pagamento ? formatDataSP(contaEstornar.data_pagamento) : '-'}</strong>
                {' · '}<span className="cel-num">{formatMoeda(contaEstornar.valor_pago ?? contaEstornar.valor)}</span>
                {contaEstornar.forma_pagamento_nome ? ` · ${contaEstornar.forma_pagamento_nome}` : ''}
              </div>
            </div>
            <div style={{ background: 'var(--card-bg)', padding: 10, borderRadius: 6, lineHeight: 1.5 }}>
              A conta volta pra <strong>Pendentes</strong> e o valor volta pro caixa de onde saiu, como uma entrada
              no turno de hoje — a saida original <strong>nao e apagada</strong>, pra o dia ja fechado continuar valendo.
              Se foi lancamento em duplicidade, depois e' so excluir a conta em Pendentes.
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Motivo (min. 3 caracteres)</label>
              <textarea
                rows={3}
                value={motivoEstorno}
                onChange={(e) => setMotivoEstorno(e.target.value)}
                style={{ width: '100%', padding: 8, border: '1px solid var(--input-border)', borderRadius: 4, background: 'var(--input-bg)', color: 'var(--input-fg)' }}
                autoFocus
              />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        aberto={!!contaExcluir}
        titulo="Excluir conta a pagar?"
        mensagem={contaExcluir ? `Excluir definitivamente "${contaExcluir.descricao}" (${formatMoeda(contaExcluir.valor)})? Esta acao nao pode ser desfeita.` : ''}
        textoConfirmar="Excluir"
        perigoso
        onConfirmar={() => void confirmarExclusao()}
        onCancelar={() => setContaExcluir(null)}
      />

      {contaWhats && (
        <ModalEnviarWhatsApp
          titulo={`Enviar Conta ${contaWhats.numero_documento ?? `#${contaWhats.id}`} pelo WhatsApp`}
          contextoNome={contaWhats.fornecedor_nome_fantasia || contaWhats.fornecedor_razao_social || contaWhats.descricao}
          contextoExtra={`Vence ${formatDataSP(contaWhats.data_vencimento)} - ${formatMoeda(contaWhats.valor)}`}
          inboxFiltro="compras"
          onClose={() => setContaWhats(null)}
          onEnviar={async (digits) => {
            await api.post(`/api/contas-pagar/${contaWhats.id}/enviar-whatsapp`, {
              jid: digits,
              mensagem: montarMensagemConta(contaWhats),
            });
          }}
        />
      )}
    </div>
  );
}

function CardKPI({ cor, icon, label, qtd, valor, nota }: { cor: string; icon: React.ReactNode; label: string; qtd: string; valor: string; nota?: string }) {
  const n = Number(qtd || 0);
  return (
    <div style={{ background: 'var(--card-bg)', borderLeft: `4px solid ${cor}`, borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: cor, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {icon}
        {label}
      </div>
      <div className="cel-num" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{formatMoeda(valor)}</div>
      <div className="cel-num" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{n} conta{n === 1 ? '' : 's'}{nota ? ` ${nota}` : ''}</div>
    </div>
  );
}
